<script setup lang="ts">
// Vue & Plugins
import { computed, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

// Components
import HeaderNavigation from './HeaderNavigation.vue'
import ContentHeader from './ContentHeader.vue'

// Stores
import { useAuthUserStore } from '../../../user/authUserStore'
import { useAuthSiteStore } from '../../../user/authSiteStore'

// Other
import { currentSiteKey } from '../../injection'
import { Site } from '../../../sites/models/site'
import EmployeeShiftOfferDialog from '../../../employee_shift/components/EmployeeShiftOfferDialog.vue'
import EmployeeArchiveDialog from '../../../employees/components/dialogs/EmployeeArchiveDialog.vue'
import EmployeeSelectDialog from '../../../employees/components/dialogs/EmployeeSelectDialog.vue'
import ShiftAssignmentDialog from '../../../shifts/components/ShiftAssignmentDialog.vue'
import { useIdle, useTimestamp } from '@vueuse/core'
import ShiftConflictDialog from '../../../shifts/components/ShiftConflictDialog.vue'
// import { useToast } from 'primevue/usetoast'

// Data
const route = useRoute()

const authUserStore = useAuthUserStore()
const { user } = storeToRefs(authUserStore)

const authSiteStore = useAuthSiteStore()
const { current } = storeToRefs(authSiteStore)

const currentSite = computed(() => current.value as Site)

provide(currentSiteKey, currentSite)

const contentContainerClass = computed(() =>
  route.meta?.useFullContainer ? '' : 'gap-x-8 px-4 pt-8 sm:px-6 lg:px-8',
)

// const toast = useToast()
// TODO: Implement logout on idle
const inactivityToastGroup = 'inactivity'
// const inactivityToastVisible = ref(false)
// const showInactivityToast = () => {
//   if (!inactivityToastVisible.value) {
//     toast.add({ group: inactivityToastGroup, severity: 'warn', summary: 'Inactivity Notice', detail: 'You have been idle for too long. You will be logged out soon.' })

//     inactivityToastVisible.value = true
//   }
// }

const IDLE_THRESHOLD_START_SECONDS = 60 // The time in seconds before the inactivity toast is shown. TODO: Increase to 180 after testing.
const IDLE_THRESHOLD_END_SECONDS = 900 // The time in seconds before the user is logged out.

const duration = IDLE_THRESHOLD_END_SECONDS * 1000
const { lastActive } = useIdle(duration)
const now = useTimestamp({ interval: 1000 })
const idleFor = computed(() => Math.floor(now.value - lastActive.value) / 1000)
const durationSeconds = computed(() => duration / 1000)
const timeElapsedPercentage = computed(
  () => (idleFor.value / durationSeconds.value) * 100,
)

const signingOut = ref(false)

watch(idleFor, async (newIdleFor) => {
  if (
    newIdleFor > IDLE_THRESHOLD_START_SECONDS &&
    newIdleFor < IDLE_THRESHOLD_END_SECONDS
  ) {
    console.log('Idle above threshold, showing inactivity timer...')

    //showInactivityToast()
  } else if (newIdleFor >= IDLE_THRESHOLD_END_SECONDS) {
    console.log('Idle for too long. Logging out...')

    if (!signingOut.value) {
      //signingOut.value = true
      //await authUserStore.signOut()
      //signingOut.value = false
    }
  } else {
    //toast.removeGroup(inactivityToastGroup)
    //inactivityToastVisible.value = false
  }
})
</script>

<template>
  <!-- TODO: THEME -->
  <div class="flex min-h-full flex-col bg-zinc-100 dark:bg-transparent">
    <HeaderNavigation v-if="user && !route.meta?.hideHeader" :user="user" />

    <!-- TODO: This should be removed as a default and specifically requested if needed by classic routes -->
    <ContentHeader v-if="!route.meta?.hideContentHeader" />

    <router-view name="contentHeader" />

    <!-- TODO: Max Width Changed from max-w-7xl (80rem) to max-w-screen-2xl (1536px) -->
    <!-- TODO: Set max width to full -->
    <!--  We need to take care to not design exclusively for very large screens -->
    <!-- TODO: Remove auto margin x -->
    <div
      class="flex w-full max-w-full items-start"
      :class="contentContainerClass"
    >
      <!-- TODO: Change height calc of vh minus nav/content bar height to be max height -->
      <aside
        v-if="!route.meta?.hideLeftColumn"
        class="sticky top-8 hidden h-[calc(100vh-theme(spacing.32))] w-56 shrink-0 overflow-y-auto lg:block"
      >
        <!-- Left column area -->
        <router-view name="leftColumn" />
      </aside>

      <main class="flex-1">
        <!-- Main area -->
        <div class="mx-auto flex w-full flex-grow">
          <!-- Layout Container -->
          <div class="flex min-w-0 flex-1 flex-col">
            <!-- Content Header -->
            <router-view />
          </div>
        </div>
      </main>

      <!-- TODO: Change max height calc of vh minus nav/content bar height to be max height-->
      <aside
        v-if="!route.meta?.hideRightColumn"
        class="sticky top-8 hidden h-[calc(100vh-theme(spacing.52))] w-96 shrink-0 overflow-auto xl:block"
      >
        <!-- Right column area -->
        <router-view name="right-column" />
      </aside>
    </div>
  </div>

  <ConfirmDialog :group="'dialog'" />

  <ConfirmPopup :group="'popup'" />

  <ConfirmPopup />

  <Toast v-if="false" :group="inactivityToastGroup" position="bottom-center">
    <template #message="slotProps">
      <div class="flex flex-auto flex-col">
        <div>
          <span class="font-semibold">{{ slotProps.message.summary }}</span>
        </div>
        <div>{{ slotProps.message.detail }}</div>
        <div>
          Timer (Seconds): {{ idleFor.toFixed(0) }} / {{ durationSeconds }}
        </div>
        <ProgressBar
          class="my-1"
          :value="timeElapsedPercentage"
          :show-value="false"
        />
      </div>
    </template>
  </Toast>

  <template v-if="current">
    <EmployeeShiftOfferDialog />

    <EmployeeArchiveDialog />

    <EmployeeSelectDialog />

    <ShiftAssignmentDialog />

    <ShiftConflictDialog />
  </template>
</template>
